import type { MouseEvent } from "react";

import { MuiButton } from "@/components/ui/mui-button";
import { MuiErrorOutlineOutlinedIcon } from "@/components/ui/mui-spec-icons";
import { MuiTooltip } from "@/components/ui/mui-tooltip";
import { MuiTypography } from "@/components/ui/mui-typography";
import { RippleButton } from "@/components/ui/ripple-button";
import { useItemsError } from "@/hooks/items-error";
import { useItemSearchVariantLoad } from "@/hooks/item-search-variant-load";
import type { CatalogListItem } from "@/lib/catalog-item";
import { UNSPECIFIED_MANUFACTURER_ID } from "@/lib/catalog-item-detail";
import { searchCardVariantThumbs } from "@/lib/catalog-item-variants";
import { maxPriceLabel } from "@/lib/target-cost-display";
import { truncateText } from "@/lib/truncate-text";
import { cn } from "@/lib/utils";
import { ItemEiLogo, ItemEiTierChips, ItemStatusOverlay } from "./item-badges";
import { ItemShortFactLine, VariantThumbStrip } from "./item-compact-card-parts";
import { ItemImage } from "./item-image";
import { ItemMaxPrice } from "./item-max-price";
import { ItemSearchActionsMenu } from "./item-search-actions-menu";
import type { ItemVariantPreviewState } from "./item-variant-preview";

// The shared item card's **Item Search island face** (#798) — the donor's
// `ItemSearchIsland/components/ItemCard/ItemCard.tsx`, which is a different
// component from the catalog page's card (`items-card-list/item-card.tsx`) and
// looks it: a 250px hero where the catalog has a 128px square, a 470px floor,
// short labels kept even when empty where the catalog spells them out with
// `N/A`, its own corner icons and kebab, and a full-width call to action.
//
// **A face of the shared card, not a fork of it** (#767 R2: additive props on
// the shared catalog components). `ItemCard` draws this only when it is handed a
// search bundle, which the Item Catalog page never does, so that page's card is
// untouched — the catalog face's code path does not pass through this file.
//
// **It never reads editor state.** The host (#811) computes every gate from the
// Active selection and hands in booleans and callbacks: whether this card holds
// the selection's item, whether the line allows card actions
// (`!isFinalized && !isSoftDeleted`) and its call to action
// (`!isFinalized && !isPerPlan && selectionId && !isSoftDeleted`), and what
// Select, Remove Selected, a card click and the menu's two verbs do — and,
// for the Max Price chip (#1216), the editor's switch and the Lennar-price
// categories.

/** `ERRORS.ITEM_VARIANTS` (`ItemSearchIsland/constants.ts`), verbatim. */
const ITEM_VARIANTS_ERROR =
  "Error loading item variants. Please click on a variant thumbnail to try again.";

const APPROVED_TOOLTIP = "Item has been reviewed and approved by the DigitalOps team.";
const IMAGE_ERROR_TOOLTIP =
  "There is an issue locating the item image. Add an image or report issue to the DigitalOps Team.";

/**
 * How wide the hero is drawn, as a `sizes` list.
 *
 * The card's width is the island grid's track, and the island's own layout is
 * the host's (#811), so this is the ramp it draws (`xs 1 / sm 2 / md 3 / lg 2 /
 * xl 3` columns, `Layout.tsx`) over the island's share of the page — the whole
 * width below `lg`, half of it from `lg`, where the editor takes the other half.
 * Overstated rather than under: the ladder's rungs are 2x apart, and one rung
 * too wide costs bytes where one too narrow costs sharpness.
 */
const ISLAND_HERO_SIZES = [
  "(min-width: 1700px) 17vw",
  "(min-width: 1200px) 25vw",
  "(min-width: 900px) 34vw",
  "(min-width: 600px) 50vw",
  "100vw",
].join(", ");

/**
 * What the island host hands each card — **present or absent as one thing**,
 * as `ItemSelectionProps` and `ItemActionsProps` are.
 */
export interface ItemSearchCard {
  /**
   * The card holds the Active selection's item. The border, the squared Core
   * corner and `Remove Selected` also need the card to be *showing* its own
   * item — the donor's `isSelected && itemId === variantId`.
   */
  isSelected: boolean;
  /** `enableCardActions` — the kebab and the two corner icons. */
  enableCardActions: boolean;
  /** `!enableCardCTAs` — the call to action greyed out. */
  ctaDisabled: boolean;
  /**
   * Whether this session edits every item regardless of division — what the
   * kebab's Edit item and Delete fall back to when its usage read fails.
   */
  mayEditAnyItem: boolean;
  /** `Select`, with the item the card is showing. Called once per click. */
  onSelect: (item: CatalogListItem) => void;
  /** `Remove Selected`, with the item the card is showing. Called once per click. */
  onRemove: (item: CatalogListItem) => void;
  /**
   * The picture or the name was clicked — open the pressed tile's item.
   *
   * An id, because it is the donor's `itemCardClickAction({ itemId: variantId
   * })`: the tile pressed, whose details may not have landed — so a card
   * mid-read, or after a failed one, still opens the variant asked for.
   * Beside it, how it was opened — the dialog's close follows it (#896 D1 b).
   */
  onCardClick: (itemId: number, opening: CardOpening) => void;
  onAddVariant: (item: CatalogListItem) => void;
  onDelete: (item: CatalogListItem) => void;
  /** The editor's target-cost switch — whether the Max Price chip shows (#1216). */
  showMaxPrice: boolean;
  /**
   * The categories whose items read `Lennar Price:` (`lennarPriceCategoryIds`),
   * computed once by the host from its category tree.
   */
  lennarPriceCategoryIds: ReadonlySet<number>;
}

/**
 * Which of the card's two openers opened its item, and whether a pointer
 * pressed it — a button clicked by Enter or Space reports a `detail` of 0.
 */
export interface CardOpening {
  opener: "picture" | "name";
  pointer: boolean;
}

const openedBy = (opener: CardOpening["opener"], event: MouseEvent): CardOpening => ({
  opener,
  pointer: event.detail !== 0,
});

export interface ItemSearchCardProps {
  /** The Item Search island's face, or nothing for the catalog's own card. */
  search?: ItemSearchCard;
}

interface ItemSearchCardFaceProps {
  /** The item on show — the previewed variant once its read has landed. */
  item: CatalogListItem;
  /** The card's own item — the one the results matched. */
  ownItem: CatalogListItem;
  search: ItemSearchCard;
  /** The card's variant preview, or nothing for a card drawn outside a grid. */
  preview?: ItemVariantPreviewState;
  className?: string;
}

export function ItemSearchCardFace({
  item: previewedItem,
  ownItem,
  search,
  preview,
  className,
}: ItemSearchCardFaceProps) {
  // The tile pressed, whether or not its read has landed: the donor keys
  // selected-ness on `variantId`, which moves on the click, so a failed read
  // still counts as previewing.
  const selectedVariantId = preview?.selectedVariantId ?? ownItem.id;
  const isSelectedItem = search.isSelected && selectedVariantId === ownItem.id;
  const thumbs = preview ? searchCardVariantThumbs(ownItem.id, preview.members) : null;
  // The strip's press reads the siblings it offers (see the hook's note).
  const group = useItemSearchVariantLoad(
    thumbs ? thumbs.thumbs.map((thumb) => thumb.id).filter((id) => id !== ownItem.id) : [],
  );
  // `isFetchingVariantsData` — the press's read, or the picked sibling's own
  // while the card has nothing of it to draw. A re-read of a sibling already
  // drawn (a stale one, past the cache's 30s) is not the donor's wait: it
  // never reads a group twice, so a held variant always switches at once.
  const siblingWaiting =
    preview?.loadingVariantId !== undefined && previewedItem.id !== preview.selectedVariantId;
  const variantLoading = group.loading || siblingWaiting;
  // `if (!variant || isFetchingVariantsData) variant = item`: the card draws its
  // own item while any read is in flight.
  const item = variantLoading ? ownItem : previewedItem;
  // `{isErrorVariantsData && <ItemsError error={ERRORS.ITEM_VARIANTS} />}`,
  // mounted while the failure holds — and no `onClose`, so it stays until the
  // next read clears it (see `useItemsError`'s note on the donor's timer).
  const variantFailed = (group.failed || (preview?.failed ?? false)) && !variantLoading;
  useItemsError(variantFailed, ITEM_VARIANTS_ERROR, { duration: Infinity });

  const manufacturerName = truncateText(item.manufacturer.name, 25);
  // The full name plus the *truncated* manufacturer, and the Unspecified one
  // left out — matched on the id, as the Add Variant preview does.
  const nameTooltip =
    item.manufacturer.id === UNSPECIFIED_MANUFACTURER_ID
      ? item.name
      : `${item.name} - ${manufacturerName}`;

  return (
    // `Card variant='outlined'` at `borderWidth: '4px'`: transparent, primary
    // when selected, `hover.border` on hover (which wins over the selected
    // blue too); `minHeight: '470px'`, `minWidth: '260px'`, `height: '100%'`,
    // a flex column, `overflow: 'unset'`. White under `text.primary`, as
    // `Paper` is.
    <div
      data-testid="item-search-card"
      data-selected={isSelectedItem || undefined}
      className={cn(
        "text-lennar-ink relative flex h-full min-h-[470px] min-w-[260px] flex-col rounded-[4px] border-4 bg-white",
        isSelectedItem ? "border-lennar-blue-600" : "border-transparent",
        "hover:border-catalog-card-hover-border",
        className,
      )}
    >
      {/* The painted shadow is the inner box's `::before`, inset −4px so it
          sits on the card's outer edge, and it clears on hover. The donor's
          `'> div': { boxShadow: isCore ? 'unset' }` reaches nothing — the
          shadow is not on that box — so a core card keeps it too. */}
      <div className="flex h-full flex-col before:pointer-events-none before:absolute before:-inset-1 before:z-0 before:rounded-[4px] before:shadow-[1px_3px_7px_0px_rgba(0,0,0,0.22)] before:content-[''] hover:before:shadow-none">
        {/* The corner strip: absolute at the card's corner, `calc(100% - 18px)`
            wide, `space-between`, over the picture. */}
        <div className="absolute top-0 left-0 z-3 flex w-[calc(100%-18px)] justify-between">
          {/* The strip's left end: the donor's 85px Core slot, or the empty
              box around a Not Recommended chip. Either way it is an in-flow
              item ahead of the right-hand group, which is what pushes that
              group right under `space-between`. The badge and both chips are
              `ItemStatusOverlay`'s, whose list chain (Not Recommended, then
              Pending, then Core) is this card's three conditions, and which
              places each against this strip as the donor's does. */}
          <div aria-hidden="true" className="w-[85px]" />
          <ItemStatusOverlay item={item} selected={isSelectedItem} />
          {search.enableCardActions && (
            <div className="flex">
              {!item.isApproved && !item.heroImage && (
                <CornerIcon
                  testId="item-search-card-image-error"
                  tooltip={IMAGE_ERROR_TOOLTIP}
                  className="text-spec-error-icon"
                />
              )}
              {item.isApproved && !item.isCore && !item.isNotRecommended && (
                <CornerIcon
                  testId="item-search-card-approved"
                  tooltip={APPROVED_TOOLTIP}
                  className="text-lennar-ink"
                />
              )}
              <ItemSearchActionsMenu
                item={item}
                disabled={variantLoading}
                mayEditAnyItem={search.mayEditAnyItem}
                onAddVariant={search.onAddVariant}
                onDelete={search.onDelete}
              />
            </div>
          )}
        </div>

        {/* `<CardActionArea>` around the hero and the EI mark: a block-level
            `ButtonBase`, positioned (the mark hangs off it), with the
            `focusHighlight` wash — `currentColor` at 0.04 on hover, 0.12 when
            keyboard-focused. The donor's area has no `onClick` of its own and
            its picture carries the click; this button carries it instead, so
            the area does on Enter what it does on a click (a named a11y break
            in #798's decision round, after #254 D2's tiles). */}
        <RippleButton
          type="button"
          data-testid="item-search-card-hero"
          onClick={(event) => search.onCardClick(selectedVariantId, openedBy("picture", event))}
          className={cn(
            "relative block w-full rounded-[inherit] text-left",
            "after:pointer-events-none after:absolute after:inset-0 after:rounded-[inherit] after:bg-current after:opacity-0 after:content-['']",
            "hover:after:opacity-[0.04] focus-visible:outline-none focus-visible:after:opacity-[0.12]",
          )}
        >
          {/* `borderBottom: '1px solid'` in `lightBorder.main`, around a
              250px `object-fit: contain` picture or the missing-image box at
              the same height — the Add Variant preview's hero, which is the
              same donor block. */}
          <div className="border-lennar-divider border-b">
            <ItemImage
              image={item.heroImage}
              itemName={item.name}
              heightPx={250}
              slotSizes={ISLAND_HERO_SIZES}
              pendingApproval={!item.isApproved}
              altStyle="island"
            />
          </div>
          <ItemEiLogo item={item} placement="island" />
        </RippleButton>

        {/* `CardContent sx={{ px: 2, py: 1, flex: '1 1 0%', minWidth: 0 }}`
            around a `justifyContent: flex-start` column. */}
        <div className="flex min-w-0 flex-1 flex-col justify-start px-4 py-2">
          {preview && thumbs && (
            <VariantThumbStrip
              arrangement="row"
              thumbs={thumbs.thumbs}
              moreCount={thumbs.moreCount}
              currentId={selectedVariantId}
              loadingId={variantLoading ? selectedVariantId : undefined}
              onPressStart={group.load}
              onSelect={preview.onSelect}
              testIdPrefix="item-search-card"
            />
          )}
          <ItemEiTierChips item={item} />
          {/* `subtitle1` at `fontWeight: 600` with `pt: 1`, in a row with
              `pb: 1`. A span rather than MUI's `h6`, as the Add Variant
              preview's is: the name below is what a reader is choosing by.
              Its `fontWeight: 600` draws at **700**: the donor loads Inter at
              300 / 400 / 500 / 700 only (`timmy/index.html:17`). */}
          <div className="flex w-full items-center justify-between pb-2">
            <MuiTypography
              data-testid="item-search-card-manufacturer"
              variant="subtitle1"
              component="span"
              className="pt-2 font-bold break-words"
            >
              {manufacturerName}
            </MuiTypography>
          </div>
          <div className="w-full">
            {/* `body1`, `color='black'`, `cursor: 'pointer'`, `pb: 0.5`, with
                the click on the whole paragraph and the tooltip on the words.
                The click is a full-width button here, so the line opens the
                item from the keyboard as well (the same a11y break as the
                hero). */}
            <MuiTypography variant="body1" className="pb-1 break-words text-black">
              <button
                type="button"
                data-testid="item-search-card-name"
                onClick={(event) => search.onCardClick(selectedVariantId, openedBy("name", event))}
                className="w-full cursor-pointer text-left"
              >
                <MuiTooltip title={nameTooltip} side="top" asChild>
                  <span>{truncateText(item.name, 80)}</span>
                </MuiTooltip>
              </button>
            </MuiTypography>
            {/* `ItemMetadata`'s chip (`ItemCard.tsx:350-354`), for the item
                on show — the previewed variant once its read lands. */}
            <ItemMaxPrice
              maxPrice={item.maxPrice}
              show={search.showMaxPrice}
              label={maxPriceLabel(item.category?.id, search.lennarPriceCategoryIds)}
            />
            <ItemShortFactLine
              testId="item-search-card-line-category"
              label="Category"
              value={item.category?.name}
            />
            <ItemShortFactLine
              testId="item-search-card-line-model"
              label="Model #"
              value={item.modelNumber}
            />
            <ItemShortFactLine
              testId="item-search-card-line-jde"
              label="Item #"
              value={item.jdeItemNumber}
            />
            <ItemShortFactLine
              testId="item-search-card-line-finish"
              label="Color"
              value={item.finish?.name}
            />
          </div>
        </div>

        {/* `CardActions sx={{ px: 2, pb: 1.5 }}` over MUI's 8px. One handler,
            one call per click (#767 R1). The scroll to the top of the results
            after Select is the host's scroller's (#811), not the card's. */}
        <div className="flex items-center px-4 pt-2 pb-3">
          <MuiButton
            fullWidth
            variant={isSelectedItem ? "outlined" : "contained"}
            disabled={search.ctaDisabled || variantLoading}
            onClick={() => (isSelectedItem ? search.onRemove(item) : search.onSelect(item))}
          >
            {isSelectedItem ? "Remove Selected" : "Select"}
          </MuiButton>
        </div>
      </div>
    </div>
  );
}

/**
 * One of the strip's two round marks — react-ui's `Icon` around
 * `ErrorOutlineOutlinedIcon` at its default 24px, with `mt: 1`, a round
 * `rgba(255, 255, 255, 0.75)` ground and the caller's ink, and a tooltip.
 *
 * The approved mark is the **same glyph** as the image-error one in another
 * ink; that is the donor's choice, kept.
 */
function CornerIcon({
  testId,
  tooltip,
  className,
}: {
  testId: string;
  tooltip: string;
  className: string;
}) {
  return (
    <MuiTooltip title={tooltip} asChild>
      <span
        data-testid={testId}
        className={cn(
          "bg-catalog-card-corner-wash mt-2 inline-flex size-6 rounded-full",
          className,
        )}
      >
        <MuiErrorOutlineOutlinedIcon className="size-6" />
      </span>
    </MuiTooltip>
  );
}
